import { createClient, type Session, type SupabaseClient, type User } from '@supabase/supabase-js';
import {
  impersonationAwareFetch,
  initializeBrowserImpersonation,
  readBrowserImpersonation,
  type BrowserImpersonationState,
} from '@command-center/auth-sdk/browser-impersonation';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { AuthScreen, AuthSignInForm } from '@command-center/ui-system';
import { portalAuthUrl } from '@command-center/portal-registry';
import { ecorpShellConfig } from './shell.config';

type AccessState = 'checking' | 'allowed' | 'denied';
interface AccessActor {
  userId: string;
  email?: string;
  name?: string;
  roles: string[];
}
interface AuthValue {
  loading: boolean;
  configured: boolean;
  session: Session | null;
  user: User | null;
  actor?: AccessActor;
  impersonation: BrowserImpersonationState | null;
  clientId?: string;
  access: AccessState;
  error?: string;
  signIn(email: string, password: string): Promise<string | null>;
  requestPasswordReset(email: string): Promise<{ ok: true } | { ok: false; error: string }>;
  signOut(): Promise<void>;
}
const AuthContext = createContext<AuthValue | null>(null);
let client: SupabaseClient | null | undefined;
export const apiBase = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

function supabase(): SupabaseClient | null {
  if (client !== undefined) return client;
  const url = import.meta.env.VITE_SUPABASE_URL;
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
  client =
    url && key
      ? createClient(url, key, {
          auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
        })
      : null;
  return client;
}

export function getPasswordResetRedirectUrl(): string {
  return portalAuthUrl('ecorp', 'recovery');
}

export async function authedFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const impersonated = await impersonationAwareFetch(input, init);
  if (impersonated) return impersonated;
  const instance = supabase();
  let session = instance ? (await instance.auth.getSession()).data.session : null;
  const send = () => {
    const headers = new Headers(init.headers);
    if (session?.access_token) headers.set('Authorization', `Bearer ${session.access_token}`);
    return fetch(input, { ...init, headers });
  };
  let response = await send();
  if (response.status === 401 && instance) {
    session = (await instance.auth.refreshSession()).data.session;
    if (session) response = await send();
  }
  return response;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState<AccessState>('checking');
  const [error, setError] = useState<string>();
  const [actor, setActor] = useState<AccessActor>();
  const [impersonation, setImpersonation] = useState<BrowserImpersonationState | null>(null);
  const [clientId, setClientId] = useState<string>();
  const configured = Boolean(
    import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_ANON_KEY,
  );
  const verify = useCallback(async (next: Session | null) => {
    if (!next && !readBrowserImpersonation()) {
      setAccess('checking');
      setActor(undefined);
      setClientId(undefined);
      return;
    }
    setAccess('checking');
    try {
      const response = await authedFetch(`${apiBase}/api/auth/me`);
      const body = (await response.json()) as {
        hasAppAccess?: boolean;
        clientId?: string;
        actor?: AccessActor;
        error?: { message?: string };
      };
      if (response.ok && body.hasAppAccess) {
        setAccess('allowed');
        setActor(body.actor);
        setClientId(body.clientId);
        setError(undefined);
      } else {
        setAccess('denied');
        setActor(undefined);
        setClientId(undefined);
        setError(body.error?.message ?? 'Your account is not authorized for ECORP.');
      }
    } catch {
      setAccess('denied');
      setError('The ECORP API is unavailable.');
    }
  }, []);
  useEffect(() => {
    let disposed = false;
    let unsubscribe: (() => void) | undefined;
    void initializeBrowserImpersonation(apiBase).then(async (supportSession) => {
      if (disposed) return;
      setImpersonation(supportSession);
      if (supportSession) {
        setLoading(false);
        await verify(null);
        return;
      }
      const instance = supabase();
      if (!instance) {
        setLoading(false);
        setAccess('denied');
        return;
      }
      const { data: current } = await instance.auth.getSession();
      if (disposed) return;
      setSession(current.session);
      setLoading(false);
      void verify(current.session);
      const { data } = instance.auth.onAuthStateChange((_event, next) => {
        setSession(next);
        if (next) void verify(next);
        else setAccess('checking');
      });
      unsubscribe = () => data.subscription.unsubscribe();
    }).catch((problem: unknown) => {
      if (disposed) return;
      setLoading(false);
      setAccess('denied');
      setError(problem instanceof Error ? problem.message : 'The support session could not be opened.');
    });
    return () => {
      disposed = true;
      unsubscribe?.();
    };
  }, [verify]);
  const value = useMemo<AuthValue>(
    () => ({
      loading,
      configured,
      session,
      user: session?.user ?? null,
      actor,
      impersonation,
      clientId,
      access,
      error,
      signIn: async (email, password) => {
        const instance = supabase();
        if (!instance) return 'Supabase is not configured.';
        const result = await instance.auth.signInWithPassword({ email, password });
        if (result.error || !result.data.session) return result.error?.message ?? 'Sign-in failed.';
        setSession(result.data.session);
        await verify(result.data.session);
        return null;
      },
      requestPasswordReset: async (email) => {
        const instance = supabase();
        if (!instance) return { ok: false, error: 'Supabase is not configured.' };
        const { error: resetError } = await instance.auth.resetPasswordForEmail(email, {
          redirectTo: getPasswordResetRedirectUrl(),
        });
        return resetError ? { ok: false, error: resetError.message } : { ok: true };
      },
      signOut: async () => {
        await supabase()?.auth.signOut();
        setSession(null);
        setActor(undefined);
        setClientId(undefined);
        setAccess('checking');
      },
    }),
    [loading, configured, session, actor, impersonation, clientId, access, error, verify],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('AuthProvider missing.');
  return value;
}

export function SignIn() {
  const auth = useAuth();

  return (
    <AuthScreen
      branding={ecorpShellConfig.branding}
      description="Projects, clients, services, and requests in one secure workspace."
    >
      <AuthSignInForm
        description="Use your Command Center account to access ECORP."
        configured={auth.configured}
        configurationMessage="Supabase frontend variables are not configured."
        onSignIn={async (email, password) => {
          const error = await auth.signIn(email, password);
          return error ? { ok: false, error } : { ok: true };
        }}
        onForgotPassword={auth.requestPasswordReset}
      />
    </AuthScreen>
  );
}
